/**
 * @module source-editing/sourceediting
 */

import {Plugin, PendingActions} from '@oracle/ortl-core';
import {ButtonView} from '@oracle/ortl-ui';
import {createElement, ElementReplacer} from '@oracle/ortl-utils';

import '../theme/sourceediting.css';
import sourceEditingIcon from '../theme/icons/source-editing.svg';

import {formatHtml} from './utils/formathtml.js';

const COMMAND_FORCE_DISABLE_ID = 'SourceEditingMode';

/**
 * The source editing feature.
 *
 * It provides the possibility to view and edit the source of the document.
 *
 * For a detailed overview, check the {@glink features/source-editing source editing feature documentation} and the
 * {@glink api/source-editing package page}.
 *
 * @extends module:core/plugin~Plugin
 */
export default class SourceEditing extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'SourceEditing';
	}

	/**
	 * @inheritDoc
	 */
	static get requires() {
		return [PendingActions];
	}

	static isFormattedHtml(data) {
		return /[\r\n]/.test(data);
	}

	static formatHtml(data) {
		return formatHtml(data);
	}

	get config() {
		return this.editor.config.get('sourceEditing');
	}

	/**
	 * @inheritDoc
	 */
	constructor(editor) {
		super(editor);

		/**
		 * Flag indicating whether the document source mode is active.
		 *
		 * @observable
		 * @member {Boolean}
		 */
		this.set('isSourceEditingMode', false);

		/**
		 * The element replacer instance used to replace the editing roots with the wrapper elements containing the document source.
		 *
		 * @private
		 * @member {module:utils/elementreplacer~ElementReplacer}
		 */
		this._elementReplacer = new ElementReplacer();

		/**
		 * Maps all root names to wrapper elements containing the document source.
		 *
		 * @private
		 * @member {Map.<String,HTMLElement>}
		 */
		this._replacedRoots = new Map();

		/**
		 * Maps all root names to their editors
		 *
		 * @private
		 * @member {Map.<String,Object>}
		 */
		this._editors = new Map();

		/**
		 * Maps all root to original data.
		 *
		 * @private
		 * @member {Map.<String,String>}
		 */
		this._originalData = new Map();

		/**
		 * Maps all root names to their real data
		 * @type {Map<string, string>}
		 * @private
		 */
		this._data = new Map();

		this._onSourceDataChange = this._onSourceDataChange.bind(this);

		editor.config.define('sourceEditing', {
			loadData: this._loadDataFromWysiwygEditor.bind(this),
			saveData: this._saveDataToWysiwygEditor.bind(this),
			language: 'html',
		});
	}

	/**
	 * @inheritDoc
	 */
	init() {
		const editor = this.editor;
		const t = editor.t;

		editor.ui.componentFactory.add('sourceEditing', (locale) => {
			const buttonView = new ButtonView(locale);

			buttonView.set({
				label: t('Source'),
				icon: sourceEditingIcon,
				tooltip: true,
				class: 'ck-source-editing-button',
			});

			buttonView.bind('isOn').to(this, 'isSourceEditingMode');

			// The button should be disabled if one of the following conditions is met:
			buttonView
				.bind('isEnabled')
				.to(
					this,
					'isEnabled',
					editor.plugins.get(PendingActions),
					'hasAny',
					(isEnabled, hasAnyPendingActions) => {
						// (1) The plugin itself is disabled.
						if (!isEnabled) {
							return false;
						}

						// (2) Any pending action is scheduled. It may change the model, so modifying the document source should be prevented
						// until the model is finally set.
						if (hasAnyPendingActions) {
							return false;
						}

						return true;
					},
				);

			this.listenTo(buttonView, 'execute', () => {
				this.isSourceEditingMode = !this.isSourceEditingMode;
			});

			return buttonView;
		});

		// Currently, the plugin handles the source editing mode by itself only for the classic editor. To use this plugin with other
		// integrations, listen to the `change:isSourceEditingMode` event and act accordingly.
		if (this._isAllowedToHandleSourceEditingMode()) {
			this.on('change:isSourceEditingMode', (evt, name, isSourceEditingMode) => {
				if (isSourceEditingMode) {
					this._showSourceEditing();
					this._disableCommands();
				} else {
					this._hideSourceEditing();
					this._enableCommands();
				}
			});

			this.on('change:isEnabled', (evt, name, isEnabled) => this._handleReadOnlyMode(!isEnabled));

			this.listenTo(editor, 'change:isReadOnly', (evt, name, isReadOnly) => this._handleReadOnlyMode(isReadOnly));
		}

		this.on('change:sourceData', this._onSourceDataChange);
	}

	/**
	 * @inheritDoc
	 */
	afterInit() {
		const editor = this.editor;

		const collaborationPluginNamesToWarn = [
			'RealTimeCollaborativeEditing',
			'CommentsEditing',
			'TrackChangesEditing',
			'RevisionHistory',
		];

		// Currently, the basic integration with Collaboration Features is to display a warning in the console.
		if (collaborationPluginNamesToWarn.some((pluginName) => editor.plugins.has(pluginName))) {
			console.warn(
				'You initialized the editor with the source editing feature and at least one of the collaboration features. ' +
					'Please be advised that the source editing feature may not work, and be careful when editing document source ' +
					'that contains markers created by the collaboration features.',
			);
		}

		// Restricted Editing integration can also lead to problems. Warn the user accordingly.
		if (editor.plugins.has('RestrictedEditingModeEditing')) {
			console.warn(
				'You initialized the editor with the source editing feature and restricted editing feature. ' +
					'Please be advised that the source editing feature may not work, and be careful when editing document source ' +
					'that contains markers created by the restricted editing feature.',
			);
		}
	}

	_createCodeEditor(parent, rootName, data, isReadOnly) {
		const language = this.config.language;
		return new window.CodeMirror({
			parentElement: parent,
			language: language === 'html' ? window.CodeMirror.Language.HTML : window.CodeMirror.Language.JAVASCRIPT,
			data,
			isReadOnly,
			onDataChange: (data) => {
				this.fire('change:sourceData', 'sourceData', data, rootName);
			},
		});
	}

	_createErrorMessage(domRootElement) {
		const errorMessage = createElement(
			domRootElement.ownerDocument,
			'div',
			{
				class: 'ck-source-editing-error-message',
			},
			['Rich text editing unavailable because of unsupported HTML input.'],
		);
		errorMessage.addEventListener('click', () => this._showErrorMessage(false));

		return errorMessage;
	}

	/**
	 * Creates source editing wrappers that replace each editing root. Each wrapper contains the document source from the corresponding
	 * root.
	 *
	 * The wrapper element contains a textarea and it solves the problem, that the textarea element cannot auto expand its height based on
	 * the content it contains. The solution is to make the textarea more like a plain div element, which expands in height as much as it
	 * needs to, in order to display the whole document source without scrolling. The wrapper element is a parent for the textarea and for
	 * the pseudo-element `::after`, that replicates the look, content, and position of the textarea. The pseudo-element replica is hidden,
	 * but it is styled to be an identical visual copy of the textarea with the same content. Then, the wrapper is a grid container and both
	 * of its children (the textarea and the `::after` pseudo-element) are positioned within a CSS grid to occupy the same grid cell. The
	 * content in the pseudo-element `::after` is set in CSS and it stretches the grid to the appropriate size based on the textarea value.
	 * Since both children occupy the same grid cell, both have always the same height.
	 *
	 * @private
	 */
	_showSourceEditing() {
		const editor = this.editor;
		const config = this.config;
		const editingView = editor.editing.view;
		const model = editor.model;

		model.change((writer) => {
			writer.setSelection(null);
			writer.removeSelectionAttribute(model.document.selection.getAttributeKeys());
		});

		// It is not needed to iterate through all editing roots, as currently the plugin supports only the Classic Editor with a single
		// main root, but this code may help understand and use this feature in external integrations.
		for (const [rootName, domRootElement] of editingView.domRoots) {
			const data = config.loadData(rootName);
			this._data.set(rootName, data);
			this._originalData.set(rootName, data);

			const domSourceEditingElementWrapper = createElement(
				domRootElement.ownerDocument,
				'div',
				{
					class: 'ck-source-editing-area ck-content',
				},
				[this._createErrorMessage(domRootElement)],
			);

			const codeEditor = this._createCodeEditor(
				domSourceEditingElementWrapper,
				rootName,
				data,
				editor.isReadOnly,
			);
			this._editors.set(rootName, codeEditor);

			editor.ui.focusTracker.add(codeEditor.contentDOM);
			codeEditor.contentDOM.addEventListener('focus', () => this._showErrorMessage(false));

			editingView.change((writer) => {
				writer.addClass('ck-hidden', editingView.document.getRoot(rootName));
			});

			this._replacedRoots.set(rootName, domSourceEditingElementWrapper);

			this._elementReplacer.replace(domRootElement, domSourceEditingElementWrapper);
		}

		this._focusSourceEditing();
	}

	/**
	 * Restores all hidden editing roots and sets the source data in them.
	 *
	 * @private
	 */
	_hideSourceEditing() {
		const editor = this.editor;
		const editingView = editor.editing.view;

		this._updateWysiwygData();

		editingView.change((writer) => {
			for (const [rootName] of this._replacedRoots) {
				writer.removeClass('ck-hidden', editingView.document.getRoot(rootName));
				const codeEditor = this._editors.get(rootName);
				editor.ui.focusTracker.remove(codeEditor.contentDOM);
			}
		});

		this._elementReplacer.restore();

		this._originalData.clear();
		this._data.clear();

		this._replacedRoots.clear();
		this._editors.clear();

		editingView.focus();
	}

	_onSourceDataChange(evt, property, data, rootName) {
		this._data.set(rootName, data);
		this.fire('change:data', 'data', data, rootName);
	}

	/**
	 * Updates the source data in all hidden editing roots.
	 *
	 * @private
	 */
	_updateWysiwygData() {
		const config = this.config;
		const dataToSave = {};

		this._data.forEach((newData, rootName) => {
			if (this._originalData.get(rootName) !== newData) {
				dataToSave[rootName] = newData;
			}
		});

		if (Object.keys(dataToSave).length) {
			config.saveData(dataToSave);
		}
	}

	_updateSourceData() {
		this.off('change:sourceData', this._onSourceDataChange);
		for (const [rootName] of this._data) {
			const sourceEditor = this._editors.get(rootName);
			const newData = this._data.get(rootName);
			const oldData = sourceEditor.getData();
			if (newData !== oldData) {
				sourceEditor.setData(newData);
			}
		}
		this.on('change:sourceData', this._onSourceDataChange);
	}

	_loadDataFromWysiwygEditor(rootName = 'main') {
		const data = this.editor.data.get({rootName});
		const isHtml = this.config.language === 'html';
		return isHtml && !SourceEditing.isFormattedHtml(data) ? formatHtml(data) : data;
	}

	_saveDataToWysiwygEditor(data) {
		this.editor.data.set(data, {batchType: {isUndoable: true}});
	}

	/**
	 * Focuses the textarea containing document source from the first editing root.
	 *
	 * @private
	 */
	_focusSourceEditing() {
		const [editor] = this._editors.values();
		editor.focus();
	}

	/**
	 * Disables all commands.
	 *
	 * @private
	 */
	_disableCommands() {
		const editor = this.editor;

		for (const command of editor.commands.commands()) {
			command.forceDisabled(COMMAND_FORCE_DISABLE_ID);
		}
	}

	/**
	 * Clears forced disable for all commands, that was previously set through {@link #_disableCommands}.
	 *
	 * @private
	 */
	_enableCommands() {
		const editor = this.editor;

		for (const command of editor.commands.commands()) {
			command.clearForceDisabled(COMMAND_FORCE_DISABLE_ID);
		}
	}

	/**
	 * Adds or removes the `readonly` attribute from the textarea from all roots, if document source mode is active.
	 *
	 * @param {Boolean} isReadOnly Indicates whether all textarea elements should be read-only.
	 */
	_handleReadOnlyMode(isReadOnly) {
		if (!this.isSourceEditingMode) {
			return;
		}
		for (const [, editor] of this._editors) {
			editor.setReadOnly(isReadOnly);
		}
	}

	/**
	 * Checks, if the plugin is allowed to handle the source editing mode by itself. Currently, the source editing mode is supported only
	 * for the {@link module:editor-classic/classiceditor~ClassicEditor classic editor}.
	 *
	 * @private
	 * @returns {Boolean}
	 */
	_isAllowedToHandleSourceEditingMode() {
		const editor = this.editor;
		const editable = editor.ui.view.editable;

		// Checks, if the editor's editable belongs to the editor's DOM tree.
		return editable && !editable._hasExternalElement;
	}

	_showErrorMessage(show, rootName = 'main') {
		this._replacedRoots.get(rootName)?.classList.toggle('show-error', show);
	}

	/**
	 * Insert text at cursor position
	 * @param string text
	 * @param string [rootName='main']
	 */
	insertData(data, rootName = 'main') {
		const sourceEditor = this._editors.get(rootName);
		sourceEditor.insertData(data);
	}

	/**
	 * Get content of editor
	 * @param {string} rootName
	 * @return {string}
	 */
	getData(rootName = 'main') {
		return this._data.get(rootName);
	}

	/**
	 * Sets entire content of editor
	 * @param {string} data
	 * @param {string} rootName
	 */
	setData(data, rootName = 'main') {
		if (this._data.get(rootName) !== data) {
			this._data.set(rootName, data);
			this._updateSourceData();
			this.fire('change:data', 'data', data, rootName);
		}
	}
}
